import { useQuery, useMutation, useQueryClient  } from '@tanstack/react-query';
import { useApiClient } from '@/shared/api';
import { type QAComment, type QAEntityType, type CallWithDetails } from '@intone/shared';

/**
 * Hook to fetch QA comments for an entity
 */
export function useQAComments(entityId: string, entityType: QAEntityType = 'call'){
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['qa-comments', entityId, entityType],
    queryFn: async () => {
      return await apiClient<QAComment[]>(`/qa-comments/${entityId}?entity_type=${entityType}`);
    },
    placeholderData: () => {
      if (entityType === 'call') {
        return queryClient.getQueryData<CallWithDetails>(['call', entityId])?.qa_comments || undefined
      }
      return undefined;
    },
    enabled: !!entityId,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to add a QA comment
 */
export function useAddQAComment() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: QAComment) => {
      return apiClient<{ comment: QAComment, message: string }>('/qa-comments', {
        method: 'POST',
        body: JSON.stringify({
          entity_id: data.entity_id,
          entity_type: data.entity_type,
          message: data.message,
        }),
      })
    },
    onMutate: async (newItem) => {
      const queryKey = ['qa-comments', newItem.entity_id, newItem.entity_type];
      await queryClient.cancelQueries({ queryKey });

      const previousList = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: QAComment[] = []) => [
        ...old,
        {...newItem},
      ]);

      return { previousList, queryKey };
    },
    onSuccess: (realNewItem, _, context) => {
      queryClient.setQueryData(context.queryKey, (old: QAComment[] = []) =>
        old.map(item => item.id === 1 ? realNewItem.comment : item)
      );
    },
    onError: (_, __, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(context.queryKey, context.previousList);
      }
    },
  });
}

/**
 * Hook to update a QA comment
 */
export function useUpdateQAComment() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: QAComment) => {
      return apiClient<{ comment: QAComment, message: string }>('/qa-comments', {
        method: 'PUT',
        body: JSON.stringify({
          entity_id: data.entity_id,
          entity_type: data.entity_type,
          message: data.message,
          comment_id: data.id,
        }),
      })
    },
    onMutate: async (updatedItem) => {
      const queryKey = ['qa-comments', updatedItem.entity_id, updatedItem.entity_type];
      await queryClient.cancelQueries({ queryKey });

      const previousList = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: QAComment[] = []) =>
        old.map(item => item.id === updatedItem.id ? { ...item, ...updatedItem } : item)
      );

      return { previousList, queryKey };
    },
    onSuccess: (realUpdatedItem, _, context) => {
      queryClient.setQueryData(context.queryKey, (old: QAComment[] = []) =>
        old.map(item => item.id === realUpdatedItem.comment.id ? realUpdatedItem.comment : item)
      );
    },
    onError: (_, __, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(context.queryKey, context.previousList);
      }
    },
  });
}
